import { useTranslations } from 'next-intl';
import React, { RefObject, useEffect, useRef } from 'react';

import useScrollState, { ScrollState } from '@cineamo/ui/src/hooks/useScrollState';

import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';
import { IconForth24 } from '@cineamo/ui/src/icons/ic-forth';

import { cn } from '../helpers/cn';

import ButtonWithIcon, { ButtonWithIconStyle } from './ButtonWithIcon';

const PENDING_TARGET_MAX_AGE_MS = 1000;

type BackAndForthButtonOverlayProps = {
    scrollContainerRef?: RefObject<HTMLElement | null>;
    renderOutside?: boolean;
    buttonWithIconStyle?: ButtonWithIconStyle;
    onBackClick?: () => void;
    onForthClick?: () => void;
    buttonUnavailableStyle?: 'disabled' | 'hidden';
    renderOnMobile?: boolean;
    orientation?: 'horizontal' | 'vertical';
    scrollDistance?: number | 'container';
};

function BackAndForthButtonOverlay(
    props: BackAndForthButtonOverlayProps,
    staticScrollState?: ScrollState
): React.JSX.Element {
    const {
        scrollContainerRef,
        renderOutside,
        buttonWithIconStyle,
        buttonUnavailableStyle = 'hidden',
        renderOnMobile,
        orientation = 'horizontal',
        scrollDistance = 300
    } = props;

    const isVertical = orientation === 'vertical';

    const t = useTranslations();
    const scrollState =
        staticScrollState ??
        useScrollState({
            scrollContainerRef,
            orientation
        }).scrollState;

    // During a smooth scroll animation scrollLeft/scrollTop report mid-animation values, so
    // rapid clicks would compound to less than one step each. Base consecutive clicks on the
    // last requested target instead, as long as it is fresh enough to still be animating.
    const pendingScrollTarget = useRef<{ value: number; at: number } | null>(null);

    // Manual scroll input (wheel, touch, drag) moves the container away from the pending
    // target, so basing the next click on it would jump from a stale position. These events
    // only fire for user input — never for programmatic scrollTo — so click chaining is safe.
    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current;
        if (!scrollContainer) {
            return;
        }
        const invalidatePendingTarget = () => {
            pendingScrollTarget.current = null;
        };
        const eventNames = ['wheel', 'touchstart', 'pointerdown'] as const;
        eventNames.forEach((eventName) =>
            scrollContainer.addEventListener(eventName, invalidatePendingTarget, { passive: true })
        );
        return () => {
            eventNames.forEach((eventName) => scrollContainer.removeEventListener(eventName, invalidatePendingTarget));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollContainerRef?.current]);

    function scrollByStep(direction: -1 | 1) {
        const scrollContainer = scrollContainerRef?.current;
        if (!scrollContainer) {
            return;
        }
        const step =
            scrollDistance === 'container'
                ? isVertical
                    ? scrollContainer.clientHeight
                    : scrollContainer.clientWidth
                : scrollDistance;
        const currentPosition = isVertical ? scrollContainer.scrollTop : scrollContainer.scrollLeft;
        const maxPosition = isVertical
            ? scrollContainer.scrollHeight - scrollContainer.clientHeight
            : scrollContainer.scrollWidth - scrollContainer.clientWidth;
        const pending = pendingScrollTarget.current;
        const base = pending && Date.now() - pending.at < PENDING_TARGET_MAX_AGE_MS ? pending.value : currentPosition;
        const target = Math.min(Math.max(base + direction * step, 0), maxPosition);
        pendingScrollTarget.current = { value: target, at: Date.now() };
        scrollContainer.scrollTo({
            ...(isVertical ? { top: target } : { left: target }),
            behavior: 'smooth'
        });
    }

    function onBackClick() {
        scrollByStep(-1);
        props.onBackClick?.();
    }

    function onForthClick() {
        scrollByStep(1);
        props.onForthClick?.();
    }

    const hideStartButton = scrollState.startReached === true && buttonUnavailableStyle === 'hidden';
    const hideEndButton = scrollState.endReached === true && buttonUnavailableStyle === 'hidden';

    return (
        <div
            className={cn(
                renderOnMobile ? 'flex' : 'hidden md:flex',
                'absolute top-0 w-full h-full pointer-events-none',
                isVertical ? 'flex-col items-center justify-between' : 'items-center justify-between'
            )}>
            {!hideStartButton && (
                <ButtonWithIcon
                    className={cn('pointer-events-auto', {
                        '-translate-x-48': renderOutside && !isVertical,
                        '-translate-y-48': renderOutside && isVertical
                    })}
                    icon={<IconBack24 className={cn({ 'rotate-90': isVertical })} />}
                    onClick={onBackClick}
                    disabled={scrollState.startReached === true && buttonUnavailableStyle === 'disabled'}
                    style={
                        buttonWithIconStyle || renderOutside
                            ? ButtonWithIconStyle.transparent
                            : ButtonWithIconStyle.default
                    }
                    accessibilityName={t('common.previous')}
                />
            )}
            {!hideEndButton && (
                <ButtonWithIcon
                    className={cn('pointer-events-auto', {
                        'ml-auto': !isVertical,
                        'mt-auto': isVertical,
                        'translate-x-48': renderOutside && !isVertical,
                        'translate-y-48': renderOutside && isVertical
                    })}
                    icon={<IconForth24 className={cn({ 'rotate-90': isVertical })} />}
                    onClick={onForthClick}
                    disabled={scrollState.endReached === true && buttonUnavailableStyle === 'disabled'}
                    style={
                        buttonWithIconStyle || renderOutside
                            ? ButtonWithIconStyle.transparent
                            : ButtonWithIconStyle.default
                    }
                    accessibilityName={t('common.next')}
                />
            )}
        </div>
    );
}

export default BackAndForthButtonOverlay;
